import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Plane, Store, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { startCheckout, errorMessage } from "@/lib/api";
import { PhotoAddon, ADDON_PRICE } from "@/components/PhotoAddon";

const PLANS = [
  { id: "user", testid: "pricing-card-user", icon: Plane, title: "Traveler", price: "9.99", accent: "#FF3B6B",
    trialDays: 7, tag: "7 days FREE", signup: "Start my free week", features: ["Search any city worldwide", "Show dates, times & operating hours", "Tap-to-call & GPS directions", "Use on up to 2 devices", "10 profile photos (20 with +$3/mo add-on)"] },
  { id: "family", testid: "pricing-card-family", icon: Users, title: "Family", price: "19.99", accent: "#00E5A3",
    trialDays: 10, tag: "10 days FREE", signup: "Start our free 10 days", features: ["Everything in Traveler", "One login for the whole family", "Use on up to 5 devices at once", "Share one trip plan & hearted places", "20 profile photos included"] },
  { id: "vendor", testid: "pricing-card-vendor", icon: Store, title: "Business / Vendor", price: "25", accent: "#FFB800",
    trialDays: 7, tag: "For venues & businesses", signup: "Sign up my business", features: ["Add unlimited business listings", "Featured placement in your city", "Post shows, hours & daily updates", "Reach travelers looking right now", "10 profile photos (20 with +$3/mo add-on)"] },
];

function blockReason(plan, user) {
  if (plan === "vendor" && user.role !== "vendor") return ["error", "Create a vendor account to list a business."];
  if (plan !== "vendor" && user.role !== "user") return ["info", "Your account already includes full access."];
  if (user.subscription_status !== "active") return null;
  if (user.promo_access) return ["info", "You already have free access with your promo code."];
  return ["info", user.has_billing ? "You're subscribed. Use Manage billing on your Account page to switch plans." : "You're already subscribed."];
}

function PlanCard({ p, busy, loggedIn, trial, onChoose }) {
  const [addon, setAddon] = useState(false);
  const price = addon ? ADDON_PRICE[p.id] : p.price;
  return (
    <div data-testid={p.testid} className="relative rounded-3xl bg-[#151C2C] border border-[#26334D] p-8 flex flex-col gap-6 card-lift">
      <div className="flex items-center justify-between">
        <span className="h-11 w-11 rounded-2xl grid place-items-center" style={{ background: `${p.accent}22`, color: p.accent }}><p.icon className="h-5 w-5" /></span>
        <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full" style={{ background: `${p.accent}22`, color: p.accent }}>{p.tag}</span>
      </div>
      <div>
        <h3 className="text-xl font-bold">{p.title}</h3>
        <div className="mt-2 flex items-baseline gap-1">
          <span data-testid={`pricing-${p.id}-price`} className="text-5xl font-extrabold tracking-tight">${price}</span><span className="text-slate-400">/month</span>
        </div>
        <p className="text-sm text-slate-400 mt-1">{p.trialDays} days free, then ${price}/mo. Cancel anytime.</p>
      </div>
      <ul className="space-y-3 text-sm text-slate-300">
        {p.features.map((f) => <li key={f} className="flex gap-2"><Check className="h-4 w-4 mt-0.5" style={{ color: p.accent }} />{f}</li>)}
      </ul>
      <div className="mt-auto">{p.id !== "family" && <PhotoAddon plan={p.id} checked={addon} onChange={setAddon} testid={`pricing-${p.id}-photo-addon`} />}</div>
      <Button data-testid={`pricing-${p.id}-cta`} disabled={busy} onClick={() => onChoose(addon && p.id !== "family")}
        className="h-12 rounded-full font-semibold text-[#0B0F19] hover:opacity-90" style={{ background: p.accent }}>
        {busy ? "Opening secure checkout…" : loggedIn ? (trial ? "Add card — start free trial" : "Subscribe now") : p.signup}
      </Button>
    </div>
  );
}

export function PricingCards() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);

  const choose = async (plan, addon) => {
    if (!user) return navigate(plan === "family" ? "/register?role=user&plan=family" : `/register?role=${plan}${addon ? "&addon=1" : ""}`);
    const block = blockReason(plan, user);
    if (block) return toast[block[0]](block[1]);
    setBusy(plan);
    try { await startCheckout(addon ? `${plan}_plus` : plan); } catch (e) { toast.error(errorMessage(e)); setBusy(null); }
  };

  return (
    <div className="grid md:grid-cols-3 gap-6">
      {PLANS.map((p) => (
        <PlanCard key={p.id} p={p} busy={busy === p.id} loggedIn={!!user} trial={!!user && (user.needs_card || (user.trial_active && user.subscription_status !== "active"))} onChoose={(addon) => choose(p.id, addon)} />
      ))}
    </div>
  );
}
